import { BaseSection } from "components/layout/Section/BaseSection";
import { NsKey } from "enums/NsKey";
import { InputFactory } from "factories/InputFactory";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { useCallback, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import { BranchCapacitiesDto, TargetedSubscriberEnum } from "api/product";
import { AutocompleteConfig, ImageUploadConfig } from "types/form/componentConfig";
import { productInputsConfig } from "../form/productInputsConfig";
import { ProductFormFieldNames } from "./productFormFieldNames";
import { ProductFormValues } from "./productFormValues";

interface ProductLayoutProps {
  isEdit: boolean;
  /** Logo already stored on the product, previewed until a new file is picked. */
  logoUrl?: string | null;
  /** Name of the current owner, so the autocomplete shows it without a lookup. */
  ownerName?: string | null;
  /** What the branch registry declares per branch code; null until loaded. */
  branchesCapacites: BranchCapacitiesDto[] | null;
}

export const ProductLayout: React.FC<ProductLayoutProps> = ({ isEdit, logoUrl, ownerName, branchesCapacites }) => {
  const { t } = useDynamicTranslation([NsKey.Product]);
  const { values, setFieldValue } = useFormikContext<ProductFormValues>();

  /* The branch is held as a referential code; the registry of the back is the
     only source telling which branches let a product target a subscriber. */
  const hasTargetedSubscriber = useMemo(
    () => !!branchesCapacites?.find(capacites => capacites.code === values.branchId)?.hasTargetedSubscriber,
    [branchesCapacites, values.branchId]
  );

  /* The back refuses a targeted subscriber on a branch that does not declare it:
     moving to such a branch resets the hidden field. Nothing is reset before the
     capabilities are loaded, so an edited product keeps its stored value. */
  useEffect(() => {
    if (branchesCapacites && !hasTargetedSubscriber && values.targetedSubscriber !== TargetedSubscriberEnum.Any) {
      setFieldValue(ProductFormFieldNames.TargetedSubscriber, TargetedSubscriberEnum.Any);
    }
  }, [branchesCapacites, hasTargetedSubscriber, values.targetedSubscriber, setFieldValue]);

  const inputsConfig = useMemo(() => {
    if (!logoUrl && !ownerName) {
      return productInputsConfig;
    }
    const logoConfig = productInputsConfig[ProductFormFieldNames.Logo] as ImageUploadConfig;
    const ownerConfig = productInputsConfig[ProductFormFieldNames.PersonId] as AutocompleteConfig;
    return {
      ...productInputsConfig,
      [ProductFormFieldNames.Logo]: { ...logoConfig, previewUrl: logoUrl },
      [ProductFormFieldNames.PersonId]: { ...ownerConfig, initialLabel: ownerName }
    };
  }, [logoUrl, ownerName]);

  const renderField = useCallback((fieldName: keyof ProductFormValues) => {
    return (
      <InputFactory config={inputsConfig} fieldName={fieldName} />
    );
  }, [inputsConfig]);

  return (
    <div className="flex flex-col gap-4">
      <BaseSection title={t('PRODUCT.SECTIONS.GENERAL_INFO')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 grid grid-cols-2 gap-4">
            {renderField(ProductFormFieldNames.Logo)}
            <div className="flex flex-col justify-between">
              {renderField(ProductFormFieldNames.Label)}
              {!isEdit && renderField(ProductFormFieldNames.Code)}
              {renderField(ProductFormFieldNames.PersonId)}
            </div>
          </div>
          <div className="col-span-2">
            {renderField(ProductFormFieldNames.Summary)}
          </div>
          <div className="col-span-2">
            {renderField(ProductFormFieldNames.FullDescription)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('PRODUCT.SECTIONS.CLASSIFICATION')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            {renderField(ProductFormFieldNames.BranchId)}
          </div>
          <div className="col-span-1">
            {renderField(ProductFormFieldNames.NoticePeriodDuration)}
          </div>
          <div className="col-span-1">
            {renderField(ProductFormFieldNames.PlateformeIds)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('PRODUCT.SECTIONS.OPTIONS')}>
        <div className="grid grid-cols-1 gap-4">
          <div className="col-span-1">
            {renderField(ProductFormFieldNames.IsTacitRenewal)}
          </div>
          <div className="col-span-1">
            {renderField(ProductFormFieldNames.IsFixedFormula)}
          </div>
          {hasTargetedSubscriber && (
            <div className="col-span-1">
              {renderField(ProductFormFieldNames.TargetedSubscriber)}
            </div>
          )}
        </div>
      </BaseSection>
    </div>
  );
};
